import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import MultiSelectDropdown from '../../../Common/MultiSelectDropdown';

// Constants for field types and operators
const FIELD_TYPES = {
  TEXT: 'text',
  CHOICE: 'choice',
  NUMBER: 'number',
  BOOLEAN: 'boolean'
};

const OPERATORS = {
  EQUALS: '=',
  NOT_EQUALS: '!=',
  GREATER_THAN: '>',
  GREATER_EQUAL: '>=',
  LESS_THAN: '<',
  LESS_EQUAL: '<=',
  IN: 'in',
  NOT_IN: 'not in',
  BETWEEN: 'between'
};


const OperatorSection = memo(({
  title,
  description,
  filters,
  sectionKey,
  formData,
  setFormData,
  isEditing = true,
  metadataFields,
  distinctValues,
  excludeColumns = [],
  onlyShowColumns = []
}) => {
  // Local state for validation errors
  const [errors, setErrors] = useState({});

  // Memoize available columns to prevent recalculation on every render
  const availableColumns = useMemo(() => {
    if (!Array.isArray(metadataFields) || metadataFields.length === 0) return [];

    return [...metadataFields]
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
      .map(field => ({
        name: field.name,
        label: field.label,
        field_type: field.field_type
      }))
      .filter(col => !excludeColumns.includes(col.name))
      .filter(col => onlyShowColumns.length === 0 || onlyShowColumns.includes(col.name));
  }, [metadataFields, excludeColumns]);

  // Get allowed operators for a field type
  const getAllowedOperators = (fieldType) => {
    switch (fieldType) {
      case FIELD_TYPES.TEXT:
      case FIELD_TYPES.CHOICE:
        return [OPERATORS.EQUALS, OPERATORS.IN, OPERATORS.NOT_IN];
      case FIELD_TYPES.BOOLEAN:
        return [OPERATORS.EQUALS];
      case FIELD_TYPES.NUMBER:
        return [
          OPERATORS.EQUALS,
          OPERATORS.NOT_EQUALS,
          OPERATORS.GREATER_THAN,
          OPERATORS.GREATER_EQUAL,
          OPERATORS.LESS_THAN,
          OPERATORS.LESS_EQUAL,
          OPERATORS.BETWEEN
        ];
      default:
        return [OPERATORS.EQUALS];
    }
  };

  // Get field type for a column
  const getFieldType = useCallback((column_name) => {
    const field = availableColumns.find(f => f.name === column_name);
    return field?.field_type || FIELD_TYPES.TEXT;
  }, [availableColumns]);

  // Get distinct values for a column
  const getDistinctValues = useCallback((column_name) => {
    let values = [];
    if (distinctValues && typeof distinctValues === 'object') {
      values = distinctValues[column_name] || [];
    }
    const fieldType = getFieldType(column_name);
    if (fieldType === FIELD_TYPES.BOOLEAN) {
      // Return normalized boolean options with display labels
      return [
        { value: true, label: 'True' },
        { value: false, label: 'False' }
      ];
    }
    // For non-boolean fields, convert values to objects with value and label
    return values.map(value => ({ value, label: String(value) }));
  }, [distinctValues, getFieldType]);

  // Parse filter value for multi-select
  const parseFilterValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return value.split(',').map(v => v.trim()).filter(v => v);
  };

  // Format filter value for storage
  const formatFilterValue = (value, operator) => {
    if (operator === OPERATORS.IN || operator === OPERATORS.NOT_IN) {
      return Array.isArray(value) ? value.join(', ') : value;
    }
    return value;
  };

  // Validate filter row
  const validateFilter = (filter, index) => {
    const fieldType = getFieldType(filter.column_name);
    const operator = filter.operator;
    const value = filter.value;

    let error = '';

    if (!filter.column_name) {
      error = 'Column is required';
    } else if (!filter.operator) {
      error = 'Operator is required';
    } else if (fieldType === FIELD_TYPES.NUMBER) {
      if (operator === OPERATORS.IN || operator === OPERATORS.NOT_IN) {
        error = 'in/not in operators not allowed for numeric fields';
      } else if (operator === OPERATORS.BETWEEN) {
        // Handle between operator validation
        if (!value) {
          error = 'Between operator requires two values';
        } else {
          const values = value.split(',').map(v => v.trim());
          if (values.length !== 2) {
            error = 'Between operator requires exactly two values';
          } else {
            const numericValues = values.map(v => parseFloat(v));
            const min = numericValues[0];
            const max = numericValues[1];
            if (isNaN(min) || isNaN(max)) {
              error = 'Both values must be valid numbers';
            } else if (min > max) {
              error = 'Minimum value must be less than or equal to maximum value';
            }
          }
        }
      } else {
        // Single numeric value validation
        if (!value || isNaN(parseFloat(value))) {
          error = 'Value must be a valid number';
        }
      }
    } else {
      // Text, choice, boolean validation
      if (operator === OPERATORS.EQUALS) {
        if (!value) {
          error = 'Value is required';
        }
      } else if (operator === OPERATORS.IN || operator === OPERATORS.NOT_IN) {
        const parsedValues = parseFilterValue(value);
        if (parsedValues.length === 0) {
          error = 'At least one value is required';
        }
      }
    }

    return error;
  };

  // Update filter data and validate
  const updateFilterData = (section, index, field, value) => {
    const newFilters = [...formData[section]];
    const newFilter = { ...newFilters[index], [field]: value };

    // Reset dependent fields when column or operator changes
    if (field === 'column_name') {
      const fieldType = getFieldType(value);
      const allowedOperators = getAllowedOperators(fieldType);
      newFilter.operator = allowedOperators[0] || OPERATORS.EQUALS;
      newFilter.value = '';
    } else if (field === 'operator') {
      newFilter.value = '';
    }

    newFilters[index] = newFilter;
    setFormData({ ...formData, [section]: newFilters });

    // Validate and update errors
    const error = validateFilter(newFilter, index);
    setErrors(prev => ({
      ...prev,
      [index]: error
    }));
  };

  // Add filter, only if columns are available
  const addFilter = useCallback((section) => {
    const firstColumn = availableColumns[0];

    const newFilter = {
      column_name: firstColumn?.name || '',
      operator: firstColumn ? getAllowedOperators(firstColumn.field_type)[0] || OPERATORS.EQUALS : OPERATORS.EQUALS,
      value: ''
    };

    const newFilters = [...formData[section], newFilter];
    setFormData({ ...formData, [section]: newFilters });

    // Validate new filter
    const error = validateFilter(newFilter, newFilters.length - 1);
    setErrors(prev => ({
      ...prev,
      [newFilters.length - 1]: error
    }));
  }, [availableColumns, formData, setFormData]);

  const removeFilter = (section, index) => {
    const newFilters = formData[section].filter((_, i) => i !== index);
    setFormData({ ...formData, [section]: newFilters });

    // Remove error for this index and shift others
    const newErrors = {};
    Object.keys(errors).forEach(key => {
      const keyNum = parseInt(key);
      if (keyNum < index) {
        newErrors[keyNum] = errors[keyNum];
      } else if (keyNum > index) {
        newErrors[keyNum - 1] = errors[keyNum];
      }
    });
    setErrors(newErrors);
  };

  // Check if filter row is valid
  const isFilterValid = (filter, index) => {
    return !errors[index] && filter.column_name && filter.operator && filter.value;
  };

  // Check if all filters are valid
  const areAllFiltersValid = () => {
    return filters.length > 0 && filters.every((filter, index) => isFilterValid(filter, index));
  };

  // Check if any filter has a value (for form submission validation)
  const hasAnyFilterValue = () => {
    return filters.some(filter => filter.column_name && filter.operator && filter.value);
  };

  // Render value input based on field type and operator
  const renderValueInput = (filter, index) => {
    const fieldType = getFieldType(filter.column_name);
    const operator = filter.operator;
    const values = getDistinctValues(filter.column_name);

    if (fieldType === FIELD_TYPES.NUMBER) {
      if (operator === OPERATORS.BETWEEN) {
        if (!isEditing && filter.value) {
          // Display mode for between
          const parts = filter.value.split(',').map(v => v.trim());
          const min = parts[0] || '';
          const max = parts[1] || '';
          return (
            <div className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700">
              {min} to {max}
            </div>
          );
        }

        return (
          <div className="flex gap-2 items-center w-full">
            <input
              type="number"
              className={`flex-1 min-w-0 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !filter.value.split(',')[0]?.trim() ? 'bg-yellow-50 border-yellow-300' : ''
              }`}
              placeholder="Min"
              value={filter.value.split(',')[0] || ''}
              onChange={(e) => {
                const parts = filter.value.split(',').map(v => v.trim());
                const max = parts[1] || '';
                const newValue = `${e.target.value},${max}`;
                updateFilterData(sectionKey, index, 'value', newValue);
              }}
              disabled={!isEditing}
            />
            <span className="text-gray-500 text-sm whitespace-nowrap">to</span>
            <input
              type="number"
              className={`flex-1 min-w-0 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !filter.value.split(',')[1]?.trim() ? 'bg-yellow-50 border-yellow-300' : ''
              }`}
              placeholder="Max"
              value={filter.value.split(',')[1] || ''}
              onChange={(e) => {
                const parts = filter.value.split(',').map(v => v.trim());
                const min = parts[0] || '';
                const newValue = `${min},${e.target.value}`;
                updateFilterData(sectionKey, index, 'value', newValue);
              }}
              disabled={!isEditing}
            />
          </div>
        );
      } else {
        if (!isEditing && filter.value) {
          // Display mode for single number
          return (
            <div className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700">
              {filter.value}
            </div>
          );
        }

        return (
          <input
            type="number"
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              !filter.value ? 'bg-yellow-50 border-yellow-300' : ''
            }`}
            placeholder="Enter number"
            value={filter.value || ''}
            onChange={(e) => updateFilterData(sectionKey, index, 'value', e.target.value)}
            disabled={!isEditing}
          />
        );
      }
    } else {
      // Text, choice, boolean fields
      if (operator === OPERATORS.IN || operator === OPERATORS.NOT_IN) {
        return (
          <MultiSelectDropdown
            options={values}
            selectedValues={parseFilterValue(filter.value)}
            onChange={(selectedValues) => {
              const formattedValue = formatFilterValue(selectedValues, operator);
              updateFilterData(sectionKey, index, 'value', formattedValue);
            }}
            placeholder="Select values..."
            disabled={!isEditing}
            isEditing={isEditing}
          />
        );
      } else {
        // Single select for = operator
        if (!isEditing && filter.value) {
          // Display mode - show the selected value nicely
          const selectedOption = values.find(v => v.value == filter.value);
          return (
            <div className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700">
              {selectedOption ? selectedOption.label : filter.value}
            </div>
          );
        }

        return (
          <div className="relative w-full">
            <select
              className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filter.value}
              onChange={(e) => updateFilterData(sectionKey, index, 'value', e.target.value)}
              disabled={!isEditing}
            >
              <option value="">Select value</option>
              {values.length > 0 ? (
                values.map(valueObj => (
                  <option key={valueObj.value} value={valueObj.value}>{valueObj.label}</option>
                ))
              ) : (
                <option disabled>No values available</option>
              )}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        );
      }
    }
  };

  // Reset invalid filters when metadataFields changes
  useEffect(() => {
    if (availableColumns.length > 0 && filters.length > 0) {
      const availableColumnNames = availableColumns.map(col => col.name);
      const hasInvalidFilters = filters.some(filter =>
        filter.column_name && !availableColumnNames.includes(filter.column_name)
      );

      if (hasInvalidFilters) {
        const newFilters = filters.map(filter => {
          if (filter.column_name && !availableColumnNames.includes(filter.column_name)) {
            return { column_name: '', operator: OPERATORS.EQUALS, value: '' };
          }
          return filter;
        });

        setFormData({ ...formData, [sectionKey]: newFilters });

        // Clear errors for reset filters
        const newErrors = {};
        newFilters.forEach((filter, index) => {
          if (filter.column_name) {
            newErrors[index] = validateFilter(filter, index);
          }
        });
        setErrors(newErrors);
      }
    }
  }, [availableColumns, filters, formData, sectionKey, setFormData]);

  return (
    <div className="mb-6">
      <div className="text-sm text-gray-600 mb-3">{description}</div>

      {filters.map((filter, index) => (
        <div key={index} className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-3 items-start">
          {/* Column Select */}
          <div className="relative sm:col-span-4">
            <select
              className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filter.column_name}
              onChange={(e) => updateFilterData(sectionKey, index, 'column_name', e.target.value)}
              disabled={!isEditing || availableColumns.length === 0}
            >
              <option value="">Select column</option>
              {availableColumns.map(column => (
                <option key={column.name} value={column.name}>{column.label}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Operator Select */}
          <div className="relative sm:col-span-2">
            <select
              className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filter.operator}
              onChange={(e) => updateFilterData(sectionKey, index, 'operator', e.target.value)}
              disabled={!isEditing || !filter.column_name || availableColumns.length === 0}
            >
              {filter.column_name ? (
                getAllowedOperators(getFieldType(filter.column_name)).map(operator => (
                  <option key={operator} value={operator}>{operator}</option>
                ))
              ) : (
                <option value="">Select column first</option>
              )}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Value Input */}
          <div className="sm:col-span-5">
            {renderValueInput(filter, index)}
          </div>

          {/* Remove Button */}
          {isEditing && (
            <div className="sm:col-span-1 flex justify-center">
              <button
                type="button"
                onClick={() => removeFilter(sectionKey, index)}
                className="text-red-500 hover:text-red-700 p-2 rounded hover:bg-red-50 transition-colors"
              >
                🗑️
              </button>
            </div>
          )}
        </div>
      ))}

      {/* Error Display - Show inline with each filter */}
      {filters.map((filter, index) => (
        errors[index] && (
          <div key={`error-${index}`} className="text-red-600 text-sm mb-3 pl-1 border-l-2 border-red-300 bg-red-50 p-2 rounded-r">
            <span className="font-medium">Filter {index + 1}:</span> {errors[index]}
          </div>
        )
      ))}

      {/* Action Buttons */}
      {isEditing && (
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => addFilter(sectionKey)}
            disabled={availableColumns.length === 0}
            className={`rounded px-4 py-2 text-sm transition-colors ${
              availableColumns.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-black text-white hover:bg-gray-800'
            }`}
          >
            Add Filter
          </button>

          {filters.length > 0 && (
            <button
              type="button"
              disabled={!areAllFiltersValid()}
              className={`rounded px-4 py-2 text-sm transition-colors ${
                areAllFiltersValid()
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              onClick={() => {
                // This could trigger a callback to notify parent that filters are ready
                console.log('Filters applied:', filters);
              }}
            >
              Apply Filters ({filters.filter((_, index) => isFilterValid(filters[index], index)).length}/{filters.length})
            </button>
          )}
        </div>
      )}

      {/* Validation Status */}
      {filters.length > 0 && (
        <div className={`text-sm mt-2 ${areAllFiltersValid() ? 'text-green-600' : 'text-red-600'}`}>
          {areAllFiltersValid() ? '✓ All filters are valid' : '⚠ Some filters have errors'}
        </div>
      )}

      {/* No Filters Message */}
      {filters.length === 0 && availableColumns.length > 0 && (
        <div className="text-sm text-gray-500 mt-2">
          No filters configured. Click "Add Filter" to start filtering your data.
        </div>
      )}

      {/* Filter Status Summary */}
      {filters.length > 0 && availableColumns.length > 0 && (
        <div className="text-sm text-gray-600 mt-2">
          {hasAnyFilterValue()
            ? `${filters.filter(f => f.column_name && f.operator && f.value).length} active filter(s)`
            : 'No active filters - all filters must have column, operator, and value selected'
          }
        </div>
      )}
    </div>
  );
});

OperatorSection.displayName = 'OperatorSection';

export default OperatorSection;
